import {
  Box,
  Button,
  Center,
  chakra,
  HStack,
  IconButton,
  Menu,
  MenuDivider,
  MenuItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Input } from '@gamma/ui/form-fields';
import ArrowForwardSharpIcon from '@gamma/ui/icons/ArrowForwardSharpIcon';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import SearchSharpIcon from '@gamma/ui/icons/SearchSharpIcon';
import { PopUpMenuLinksProps } from '@gamma/ui/navigation';
import { ErrorMessage, GraphQLReqStatus } from '@gamma/ui/progress';
import { ROUTES } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { LogScaleContext, useHumioToken } from '@investigator/shared/providers';
import { useContext, useMemo, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router';
import { useGetHumioDashboards } from '../../queries';

const { dashboardsMenu, dashboards, findDashboard, noResultsFound } =
  i18n.navigation;
const { dashboards: dashboardsRoute } = ROUTES;
const StyledLink = chakra(Link);

// Custom type for dashboard menu items with additional data attributes
type DashboardMenuItemProps = import('@chakra-ui/react').MenuItemProps & {
  'data-dashboard-name'?: string;
  'data-dashboard-id'?: string;
};

/**
 * DashboardMenu Component
 * Displays a searchable list of Humio/LogScale dashboards
 * Allows users to navigate between different dashboards and manage dashboard state
 */
export const DashboardMenu = () => {
  const { iframeKey, setIframeKey, setLogScaleDashboardParams } =
    useContext(LogScaleContext);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchText, setSearchText] = useState<string>('');
  const { token: sessionToken } = useHumioToken();
  const { data, loading, error } = useGetHumioDashboards({
    variables: { humioSessionToken: sessionToken || '' },
    skip: !sessionToken,
    fetchPolicy: 'network-only',
  });

  /**
   * Generate dashboard menu links from LogScale data
   * Sorts dashboards alphabetically and creates navigation links
   */
  const dashboardMenuLinks = useMemo<PopUpMenuLinksProps[]>(() => {
    if (!data?.getHumioDashboards?.searchDomain?.dashboards) {
      return [];
    }

    // Create "All Dashboards" link
    const allDashLink: PopUpMenuLinksProps = {
      to: dashboardsRoute(),
      label: dashboardsMenu,
      isHidden: true,
    };

    // Map and sort dashboard data into menu links
    const dashLinks = [...data.getHumioDashboards.searchDomain.dashboards]
      .sort((a, b) => {
        const nameA = a?.name ?? '';
        const nameB = b?.name ?? '';
        return nameA.localeCompare(nameB);
      })
      .map((link) => {
        if (!link) {
          return {
            to: dashboardsRoute(),
            label: '',
            menuItemProps: {} as DashboardMenuItemProps,
          };
        }
        return {
          to: dashboardsRoute(),
          label: link.name ?? '', // Ensure label is always a string
          menuItemProps: {
            'data-dashboard-name': link.name ?? '',
            'data-dashboard-id': link.id,
          } as DashboardMenuItemProps,
        };
      });

    return [allDashLink, ...dashLinks];
  }, [data]);

  /**
   * Filter dashboard links based on search text
   */
  const filteredDashboardMenuLinks = useMemo(() => {
    if (!searchText) return dashboardMenuLinks;
    return dashboardMenuLinks.filter((link) =>
      link.label.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [dashboardMenuLinks, searchText]);

  /**
   * Clear the search input
   */
  const clearSearch = () => setSearchText('');

  /**
   * Handle navigation to all dashboards view
   * Clears any selected dashboard from localStorage and context
   */
  const handleDashboardsButton = () => {
    localStorage.removeItem('dashboard_name');
    localStorage.removeItem('dashboard_id');
    setLogScaleDashboardParams?.({ name: '', dashboardId: '' });
    navigate(dashboardsRoute());
    if (setIframeKey && iframeKey !== undefined) {
      setIframeKey(iframeKey + 1);
    }
  };

  /**
   * Store selected dashboard information in localStorage
   * Used for maintaining dashboard selection across page refreshes
   */
  const storeLocalStorageDashboard = (dashName: string, dashId: string) => {
    localStorage.setItem('dashboard_name', dashName);
    localStorage.setItem('dashboard_id', dashId);
  };

  /**
   * Handle dashboard link click
   * Updates localStorage, context state, and triggers iframe refresh
   */
  const handleLinkOnClick = (dashName: string, dashId: string) => {
    storeLocalStorageDashboard(dashName, dashId);
    setLogScaleDashboardParams?.({ name: dashName, dashboardId: dashId });
    setIframeKey?.((prev) => (prev != null ? prev + 1 : 1));
  };

  return (
    <Menu>
      <VStack
        w="300px"
        h="500px"
        spacing={0}
        overflowY="auto"
        alignItems="stretch"
        _last={{ pb: 1 }}
        _first={{ pt: 0 }}
      >
        <HStack
          p={2}
          alignItems="start"
          borderBottomWidth="1px"
          borderBottomColor="border.1"
          backgroundColor="layer.2"
        >
          <Box data-testid="dashboard-menu-search-box">
            <Input
              size="sm"
              name="search"
              value={searchText}
              isLabelHidden={true}
              label={findDashboard}
              placeholder={findDashboard}
              leftElement={<SearchSharpIcon />}
              onChange={(e) => setSearchText(e.target.value)}
              data-testid="dashboard-menu-search-input"
              rightElement={
                searchText && (
                  <IconButton
                    data-testid="dashboard-menu-search-clear-btn"
                    aria-label="clear"
                    onClick={clearSearch}
                    variant="link"
                    colorScheme="gray"
                  >
                    <CloseSharpIcon />
                  </IconButton>
                )
              }
            />
          </Box>
          <Box data-testid="dashboard-menu-all-dashboards-box">
            <Button
              px={2}
              size="sm"
              w="112px"
              variant="solid"
              rightIcon={<ArrowForwardSharpIcon />}
              onClick={handleDashboardsButton}
              data-testid="dashboard-menu-all-dashboards-btn"
            >
              {dashboards}
            </Button>
          </Box>
        </HStack>

        {/* Dashboard List */}
        <Box flex="1 1 auto" data-testid="dashboard-menu-list-box">
          {loading ? (
            <Center w="100%" data-testid="loading-indicator">
              <GraphQLReqStatus
                loading={loading}
                error={error as ErrorMessage | undefined}
              />
            </Center>
          ) : error ? (
            <Box p={4} data-testid="dashboard-menu-error-message">
              <Text
                textStyle="h6"
                color="gray.500"
                data-testid="dashboard-menu-error-text"
              >
                Error fetching dashboards
              </Text>
            </Box>
          ) : filteredDashboardMenuLinks &&
            filteredDashboardMenuLinks.length > 0 ? (
            filteredDashboardMenuLinks.map((link, index) => {
              const {
                to,
                label,
                menuItemProps,
                isHidden = false,
                hasDivider = false,
              } = link;

              const menuProps = menuItemProps as
                | DashboardMenuItemProps
                | undefined;
              const dashName = menuProps?.['data-dashboard-name'] ?? label;
              const dashId = menuProps?.['data-dashboard-id'] ?? '';
              const isActive = Boolean(matchPath(to, pathname));

              return (
                <StyledLink
                  key={index}
                  to={to}
                  borderRadius={5}
                  style={{ textDecoration: 'none' }}
                  onContextMenu={() =>
                    storeLocalStorageDashboard(dashName, dashId)
                  }
                >
                  {hasDivider && <MenuDivider />}
                  <MenuItem
                    data-testid={`dashboard-menu-link-item-${index}`}
                    onClick={() => handleLinkOnClick(dashName, dashId)}
                    color={
                      isActive &&
                      dashName === localStorage.getItem('dashboard_name')
                        ? 'state.selected'
                        : 'inherit'
                    }
                    bgColor="transparent"
                    display={isHidden ? 'none' : 'flex'}
                    {...menuItemProps}
                  >
                    <Text data-testid={`dashboard-menu-link-label-${index}`}>
                      {label}
                    </Text>
                  </MenuItem>
                </StyledLink>
              );
            })
          ) : (
            <Box p={4}>
              <Text
                textStyle="h6"
                color="gray.500"
                data-testid="dashboard-menu-no-results-text"
              >
                {noResultsFound}
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
    </Menu>
  );
};
